import { useCallback, useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';

import {
  DateInputWithConfirm,
  UploadActions,
  UploadComplete as UploadCompleteSection,
  Modal,
  getStringFromDate,
  getDateFromString,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import { isValidDate } from '../../../../../utilities/validation';
import { IDate } from '../../../../../types';

interface IProps {
  closeModal: () => void;
  openUploadModal: (
    docKey: string,
    docType: string,
    sponsorType: string,
    sponsorKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => void;
  title: string;
  docType: string;
  sponsorType: string;
  isComplete: boolean;
  docKey: string;
  fileNames: string[];
  date: string | undefined;
}

export const NewVisaApplicationPdfCopyModal = (props: IProps) => {
  const {
    closeModal,
    openUploadModal: initOpenUploadModal,
    fileNames,
    docKey,
    isComplete: isDocumentApproved,
    sponsorType,
    docType,
    title,
    date: initDate
  } = props;

  const [isReceived, setIsReceived] = useState<boolean | undefined>(undefined);

  const openUploadModal = (metadata: any) => {
    initOpenUploadModal(docKey, docType, sponsorType, null, title, fileNames, metadata);
  };

  const [isComplete, setIsComplete] = useState(false);

  const [date, setDate] = useState(getDateFromString(initDate));
  const [visaStartDateError, setVisaStartDateError] = useState('');

  const checkDate = useCallback((date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  }, []);

  useEffect(() => {
    const status = Boolean(!visaStartDateError.length && checkDate(date));
    setIsComplete(status);
  }, [visaStartDateError, checkDate, date]);

  const handleClick = (question: IQuestion) => {
    setIsReceived(question.char === 'Y');
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      } else {
        return '';
      }
    }

    return '';
  };

  const returnToSection = () => {
    closeModal();
  };

  const uploadDocument = () => {
    openUploadModal({
      date: getStringFromDate(date)
    });
  };

  return (
    <Modal id={'newVisaApplicationPdfCopy'} show={true} title={title} onClose={closeModal} hideFooter>
      {isDocumentApproved ? (
        <UploadCompleteSection fileNames={fileNames} onUpload={uploadDocument} onContinue={returnToSection} />
      ) : (
        <>
          <Text>
            Once you have paid your visa application fee and your visa application is submitted you will receive an
            email copy of your submission.
          </Text>

          <RadioOptionsWithConfirm
            key={'haveLetter'}
            value={isReceived}
            label={'Were you emailed a PDF copy of your visa application after it was submitted?'}
            onClick={handleClick}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: isReceived
              },
              {
                char: 'N',
                label: 'No',
                selected: isReceived === false
              }
            ]}
          />

          {isReceived && (
            <DateInputWithConfirm
              id={`application-date`}
              label={`What date did you submit this application?`}
              value={date}
              errorText={visaStartDateError}
              onChangeDD={(evt: any) => {
                setDate({
                  ...date,
                  DD: evt.target.value
                });
              }}
              onChangeMM={(evt: any) => {
                setDate({
                  ...date,
                  MM: evt.target.value
                });
              }}
              onChangeYY={(evt: any) => {
                setDate({
                  ...date,
                  YYYY: evt.target.value
                });
              }}
              onBlur={() => setVisaStartDateError(validateDate(date))}
            />
          )}

          {isReceived === false && (
            <RequirementsRemedy>
              <Text>
                If you received assistance from an agent or immigration counsellor, please ask them to help you locate
                this file.
              </Text>

              <Text>
                If you have an exemption an non-standard entry clearance, you can click "Yes" and upload UKVI
                correspondence that states your arrangement.
              </Text>
            </RequirementsRemedy>
          )}

          {isReceived !== undefined && (
            <UploadActions
              complete={isReceived}
              disabled={!(!isReceived || (isReceived && isComplete))}
              onContinue={isReceived ? uploadDocument : returnToSection}
            />
          )}
        </>
      )}
    </Modal>
  );
};
